import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { IGroupForLessons, useTriggerZoomGroupProcess } from 'core/hooks';
import { PATHS } from 'router/config';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../../_detailShared/detail.styled';
import { GroupColors } from './groupColors';
import { weekDayLabel } from './ScheduleCard';

const ZoomAccountLine = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: ${GroupColors.textSecondary};
`;

const ZoomAccountLabel = styled.strong`
  color: ${GroupColors.textPrimary};
`;

/**
 * `GroupZoomScheduleType.weekDay` is the schema enum
 * `GroupZoomScheduleWeekDay` (`A_0`..`A_6`, Mon..Sun); `classesDays`
 * uses a plain int 1..7. Map enum → int so we can share
 * `weekDayLabel` between both surfaces.
 */
const enumWeekDayToInt = (
  weekDay: string | null | undefined,
): number | null => {
  if (!weekDay || !weekDay.startsWith('A_')) return null;
  const n = Number(weekDay.slice(2));
  return Number.isFinite(n) ? n + 1 : null;
};

interface IZoomAutoScheduleCardProps {
  group: IGroupForLessons;
  onSynced: () => void;
}

/**
 * ZoomAutoScheduleCard — Zoom auto-processing schedule slots
 * (`zoomSchedules`) + the «Force process» button that fires
 * `triggerZoomGroupProcess(groupId)` (TZ §G9). The dedicated schedule
 * editor lives at `/zoom/schedule`; here we only surface the snapshot
 * and let the operator jump there if they want to tweak slots.
 */
export const ZoomAutoScheduleCard = memo(
  ({ group, onSynced }: IZoomAutoScheduleCardProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { trigger, pending } = useTriggerZoomGroupProcess(group.id, onSynced);

    const slots = (group.zoomSchedules?.edges || [])
      .map((e) => e?.node)
      .filter((n): n is NonNullable<typeof n> => Boolean(n));

    return (
      <S.Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <S.CardTitle style={{ margin: 0 }}>
            {t('pages.groupDetail.zoom.autoScheduleTitle')}
          </S.CardTitle>
          <L.HeaderActions>
            <L.PrimaryActionButton
              type="button"
              onClick={() => history.push(PATHS.zoomSchedule)}
            >
              {t('pages.groupDetail.zoom.editSchedule')}
            </L.PrimaryActionButton>
            <L.PrimaryActionButton
              type="button"
              tone="primary"
              disabled={pending}
              onClick={() => trigger()}
            >
              {pending
                ? t('pages.groupDetail.zoom.processing')
                : t('pages.groupDetail.zoom.forceProcess')}
            </L.PrimaryActionButton>
          </L.HeaderActions>
        </div>
        <ZoomAccountLine>
          <ZoomAccountLabel>
            {t('pages.groupDetail.zoom.zoomAccount')}:{' '}
          </ZoomAccountLabel>
          {group.zoomAccount?.displayName || '—'}
        </ZoomAccountLine>
        {slots.length === 0 ? (
          <S.SubTitle style={{ marginTop: 8 }}>
            {t('pages.groupDetail.zoom.noSlots')}
          </S.SubTitle>
        ) : (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginTop: 10,
            }}
          >
            {slots.map((slot) => (
              <L.Tag key={slot.id} tone={slot.isActive ? 'ok' : 'neutral'}>
                {t(
                  `pages.groupDetail.schedule.day.${weekDayLabel(
                    enumWeekDayToInt((slot.weekDay as unknown) as string),
                  )}`,
                )}
                {slot.runTime ? ` · ${slot.runTime}` : ''}
                {slot.isActive
                  ? ''
                  : ` · ${t('pages.groupDetail.zoom.inactive')}`}
              </L.Tag>
            ))}
          </div>
        )}
      </S.Card>
    );
  },
);
